/*
 * <page-transition> – logo overlay between page loads (theme setting "page_transition_enabled").
 * A same-site link click fades the overlay in, then navigates; the next page starts covered
 * (html.kw-page-entering, set by the inline script in layout/theme.liquid before paint) and fades out.
 */
if (!customElements.get("page-transition")) {
  customElements.define(
    "page-transition",
    class PageTransition extends HTMLElement {
      connectedCallback() {
        this.html = document.documentElement;
        this.timers = [];
        this.abortController = new AbortController();
        const { signal } = this.abortController;

        if (this.html.classList.contains("kw-page-entering")) {
          this.clearFlag();
          this.timers.push(setTimeout(() => requestAnimationFrame(() => this.hide()), 150));
          // Safety net in case the first reveal didn't happen
          this.timers.push(
            setTimeout(() => {
              if (this.classList.contains("is-active") || this.html.classList.contains("kw-page-entering")) {
                requestAnimationFrame(() => this.hide());
              }
            }, 5000)
          );
        }

        document.addEventListener("click", (event) => this.onClick(event), { signal });
        window.addEventListener("pageshow", (event) => this.onPageShow(event), { signal });
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.timers.forEach(clearTimeout);
      }

      hide() {
        this.html.classList.remove("kw-page-entering");
        this.classList.add("is-active");
        void this.offsetHeight;
        this.classList.remove("is-active");
        this.html.classList.add("kw-page-revealed");
        this.clearFlag();
      }

      shouldTransition(link) {
        const href = link.getAttribute("href");
        if (!href || href.charAt(0) === "#" || /^(mailto|tel|javascript):/.test(href)) return false;
        if (link.hostname !== location.hostname) return false;
        if (link.target && link.target !== "_self") return false;
        if (link.hasAttribute("download") || link.classList.contains("no-transition")) return false;
        if (link.closest("[data-no-transition]")) return false;
        if (link.pathname === location.pathname && link.search === location.search) return false;
        if (window.Shopify?.designMode) return false;
        return true;
      }

      onClick(event) {
        if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.shiftKey) return;
        const link = event.target.closest("a");
        if (!link || !this.shouldTransition(link)) return;

        event.preventDefault();
        try {
          sessionStorage.setItem("kw-pt", "1");
        } catch (error) {
          // the next page simply won't start covered
        }
        this.classList.add("is-active");

        let navigated = false;
        const navigate = () => {
          if (navigated) return;
          navigated = true;
          window.location.href = link.href;
        };
        this.addEventListener("transitionend", navigate, { once: true });
        setTimeout(navigate, 500);
      }

      onPageShow(event) {
        if (!event.persisted) return;
        this.classList.remove("is-active");
        this.html.classList.remove("kw-page-entering", "kw-page-revealed");
        this.clearFlag();
      }

      clearFlag() {
        try {
          sessionStorage.removeItem("kw-pt");
        } catch (error) {
          // storage unavailable
        }
      }
    }
  );
}
